import React, { createContext, useState, useEffect, useContext } from 'react';
import api, { getApiBaseUrl } from '../utils/axios';
import axios from 'axios';

const AuthContext = createContext();

export { AuthContext };

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

const SELECTED_SUBSCRIBER_KEY = 'selectedSubscriberId';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUnderConstruction, setIsUnderConstruction] = useState(false);
  const [backendReachable, setBackendReachable] = useState(null); // null = unknown, true/false after first check
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersLoaded, setSubscribersLoaded] = useState(false);
  const [selectedSubscriberId, setSelectedSubscriberIdState] = useState(() =>
    localStorage.getItem(SELECTED_SUBSCRIBER_KEY) || ''
  );

  const setSelectedSubscriberId = (id) => {
    setSelectedSubscriberIdState(id || '');
    if (id) {
      localStorage.setItem(SELECTED_SUBSCRIBER_KEY, id);
    } else {
      localStorage.removeItem(SELECTED_SUBSCRIBER_KEY);
    }
  };

  const loadSubscribers = async () => {
    try {
      const response = await api.get('/api/subscribers');
      const list = response.data || [];
      setSubscribers(list);
      if (list.length > 0) {
        const current = localStorage.getItem(SELECTED_SUBSCRIBER_KEY);
        if (!current || !list.some(s => s.id === current)) {
          setSelectedSubscriberIdState(list[0].id);
          localStorage.setItem(SELECTED_SUBSCRIBER_KEY, list[0].id);
        }
      }
    } catch (err) {
      console.error('Failed to load subscribers:', err);
      setSubscribers([]);
    } finally {
      setSubscribersLoaded(true);
    }
  };

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user?.role === 'admin' || user?.role === 'subscriber') {
      loadSubscribers();
    }
  }, [user?.role]);

  const checkAuth = async () => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setUser(null);
      setLoading(false);
      setBackendReachable(null);
      try {
        const response = await axios.get(`${getApiBaseUrl()}/api/auth/status`, { withCredentials: true, timeout: 5000 });
        setBackendReachable(true);
        if (response.data.isUnderConstruction) {
          setIsUnderConstruction(true);
          setUser({ role: 'guest', isUnderConstruction: true });
        }
      } catch (e) {
        setBackendReachable(e.response === undefined);
      }
      return;
    }
    try {
      const response = await axios.get(`${getApiBaseUrl()}/api/auth/status`, {
        withCredentials: true,
        timeout: 5000
      });
      setBackendReachable(true);
      
      if (response.data.isUnderConstruction) {
        setIsUnderConstruction(true);
        setUser({ role: 'guest', isUnderConstruction: true });
      } else {
        try {
          const meResponse = await api.get('/api/auth/me');
          if (meResponse.data?.user) {
            setUser(meResponse.data.user);
          } else {
            localStorage.removeItem('authToken');
            setUser(null);
          }
        } catch (error) {
          localStorage.removeItem('authToken');
          setUser(null);
        }
      }
    } catch (error) {
      const isNetworkError = !error.response && (error.code === 'ERR_NETWORK' || error.message?.includes('Network'));
      setBackendReachable(!isNetworkError);
      if (isNetworkError) {
        console.error('Backend server is niet bereikbaar. Start de server in de projectmap met: npm start');
      } else {
        console.error('Auth check failed:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const login = (userData) => {
    const actualUser = userData.user
      ? { ...userData.user, email: userData.user.email || userData.email }
      : userData;
    setUser(actualUser);
    setIsUnderConstruction(userData.isUnderConstruction || false);
    if (userData.token) {
      localStorage.setItem('authToken', userData.token);
    }
    if (actualUser.subscriberId) {
      setSelectedSubscriberIdState(actualUser.subscriberId);
      localStorage.setItem(SELECTED_SUBSCRIBER_KEY, actualUser.subscriberId);
    }
  };

  const logout = () => {
    const token = localStorage.getItem('authToken');
    const baseUrl = getApiBaseUrl();
    // Eerst alles lokaal wissen, dan altijd doorsturen (ook als server niet reageert)
    localStorage.removeItem('authToken');
    localStorage.removeItem(SELECTED_SUBSCRIBER_KEY);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem('impersonating');
      sessionStorage.removeItem('adminTokenBeforeImpersonate');
    }
    setUser(null);
    setIsUnderConstruction(false);
    setSelectedSubscriberIdState('');
    // Server cookie laten wissen (fire-and-forget, max 2s wachten)
    const logoutUrl = `${baseUrl ? baseUrl.replace(/\/$/, '') : ''}/api/auth/logout`;
    axios.post(logoutUrl, {}, {
      withCredentials: true,
      timeout: 2000,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    }).catch(() => {});
    // Volledige herlaad naar startpagina (expliciet origin tegen cache/SPA-gedrag)
    window.location.replace(window.location.origin + '/');
  };

  // Trial / licentie helpers
  const isLicenseActive = user?.license_active === true || user?.role === 'admin';
  const trialEndsAt = user?.trial_ends_at || null;
  // Alleen verlopen als er een trial-datum IS en die in het verleden ligt
  const isTrialExpired = !isLicenseActive && !!trialEndsAt && new Date(trialEndsAt) <= new Date();
  const trialDaysLeft = trialEndsAt
    ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24)))
    : null; // null = geen trial ingesteld

  const value = {
    user,
    loading,
    isUnderConstruction,
    backendReachable,
    login,
    logout,
    checkAuth,
    subscribers,
    subscribersLoaded,
    selectedSubscriberId,
    setSelectedSubscriberId,
    loadSubscribers,
    // Trial / licentie
    isLicenseActive,
    trialEndsAt,
    isTrialExpired,
    trialDaysLeft
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
